import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { notify } from '@/components/ui/notify';
import { AccountFinal } from '@/api/entities';
import {
  fetchMarketPreferences,
  fetchMarketPlayers,
  fetchMarketLenses,
  fetchMarketOpportunities,
  previewMarketPreferences,
  patchMarketPreferences,
  pollMarketRecomputeStatus,
  deleteMyMarketLens,
  recalculateMarket,
  fetchVendorMatches,
} from '@/lib/marketExperienceApi';
import {
  currentLens,
  entitlementCategories,
  entitlementCountries,
  extractRubricWeights,
  extractSamThresholds,
  extractDisqualifiers,
  extractCompetitorSet,
  weightsSum,
  weightsToPayload,
  productCategoryOptions,
  redistributeRubricWeights,
  unwrapRows,
  formatCategoryLabel,
  lensDisplayName,
  rubricWeightLabel,
  AI_READINESS_LABELS,
  mktLabel,
} from '@/lib/marketExperience';
import { translate, useTranslation } from '@/components/utils/translations';
import { Switch } from '@/components/ui/switch';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import MarketHelpLabel from './MarketHelpLabel';

function rubricVersions(prefs) {
  return prefs?.rubric_versions || prefs?.versions || prefs?.history || [];
}

function parseVendorList(text) {
  return String(text || '')
    .split(/[\n,;]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

export default function MarketPreferencesDrawer({
  tenantId,
  lens,
  lenses: lensesProp = [],
  canEdit = false,
  open,
  onOpenChange,
  onSaved,
  onRescoring,
  forceSetup = false,
  excluded = null,
  held = null,
  lmsFamilies = [],
  lmsVendors = [],
  onLmsVendorsChange,
}) {
  const { t } = useTranslation();
  const [prefs, setPrefs] = useState(null);
  const [players, setPlayers] = useState(null);
  const [loading, setLoading] = useState(false);
  const [category, setCategory] = useState('');
  const [activeLens, setActiveLens] = useState(lens || null);
  const [lensLoading, setLensLoading] = useState(false);
  const [lensCountries, setLensCountries] = useState([]);
  const lensRequestId = useRef(0);
  const [weights, setWeights] = useState([]);
  const [competitorSet, setCompetitorSet] = useState([]);
  const [pasteBlock, setPasteBlock] = useState('');
  const [samPain, setSamPain] = useState(3);
  const [scoreThreshold, setScoreThreshold] = useState(60);
  const [isCurrentVendor, setIsCurrentVendor] = useState(false);
  const [enrolmentBelow, setEnrolmentBelow] = useState('');
  const [enrolmentAbove, setEnrolmentAbove] = useState('');
  const [sectorNotIn, setSectorNotIn] = useState([]);
  const [sectorOptions, setSectorOptions] = useState([]);
  // [2026-11-04] Positive sector choice = the lens's own `sectors` list (Tiago: "choose by sector (one or several)").
  const [selectedSectors, setSelectedSectors] = useState([]);
  const [lensSectorList, setLensSectorList] = useState([]);
  const [lenses, setLenses] = useState([]);
  const [countryNotIn, setCountryNotIn] = useState([]);
  const [financialBand, setFinancialBand] = useState('');
  const [aiReadiness, setAiReadiness] = useState('');
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reverting, setReverting] = useState(false);
  const [recalcStatus, setRecalcStatus] = useState(null);
  const [touched, setTouched] = useState({});
  const [competitorStats, setCompetitorStats] = useState({});
  const [addressableAccounts, setAddressableAccounts] = useState(null);
  const [pasting, setPasting] = useState(false);
  const [competitorLocked, setCompetitorLocked] = useState([]);
  const [lockedPaste, setLockedPaste] = useState('');

  const markTouched = (key) => setTouched((prev) => (prev[key] ? prev : { ...prev, [key]: true }));

  const applyLoadedPrefs = (data) => {
    setPrefs(data);
    const lensObj = currentLens(data);
    const entitled = entitlementCountries(data);
    setCategory(lensObj.category || '');
    setLensCountries(lensObj.countries.length ? lensObj.countries.filter((c) => !entitled.length || entitled.includes(c)) : entitled);
    setWeights(extractRubricWeights(data));
    setCompetitorSet(extractCompetitorSet(data));
    const sam = extractSamThresholds(data);
    setSamPain(sam.sam_min_pain);
    setScoreThreshold(sam.score_threshold);
    const dq = extractDisqualifiers(data);
    setIsCurrentVendor(dq.is_current_vendor);
    setEnrolmentBelow(dq.enrolment_below === '' || dq.enrolment_below == null ? '' : dq.enrolment_below);
    setEnrolmentAbove(dq.enrolment_above === '' || dq.enrolment_above == null ? '' : dq.enrolment_above);
    setSectorNotIn(asListSafe(dq.sector_not_in));
    {
      const ls = asListSafe(data?.lens?.sectors);
      setLensSectorList(ls);
      const notIn = asListSafe(dq.sector_not_in);
      setSelectedSectors(ls.filter((x) => !notIn.includes(x)));
    }
    setCountryNotIn(asListSafe(dq.country_not_in));
    setFinancialBand(dq.financial_band_below || '');
    setAiReadiness(dq.ai_readiness_below === '' || dq.ai_readiness_below == null ? '' : dq.ai_readiness_below);
    setCompetitorLocked(asListSafe(dq.competitor_locked));
    setTouched({});
    setPreview(null);
  };

  const applyLensSnapshot = (lensRecord) => {
    if (!lensRecord) return;
    const cat = lensRecord.category
      || (Array.isArray(lensRecord.categories) ? lensRecord.categories[0] : null)
      || lensRecord.lens_key
      || '';
    setCategory(cat);
    setCompetitorSet(extractCompetitorSet(lensRecord));
    const entitled = entitlementCountries(prefs);
    const fromLens = Array.isArray(lensRecord.countries) ? lensRecord.countries : [];
    setLensCountries(fromLens.length ? fromLens.filter((c) => !entitled.length || entitled.includes(c)) : entitled);
    const w = extractRubricWeights(lensRecord);
    if (w.length) setWeights(w);
    if (lensRecord.sam_min_pain != null) setSamPain(lensRecord.sam_min_pain);
    if (lensRecord.score_threshold != null) setScoreThreshold(lensRecord.score_threshold);
    const dq = extractDisqualifiers(lensRecord);
    setIsCurrentVendor(dq.is_current_vendor);
    setEnrolmentBelow(dq.enrolment_below === '' || dq.enrolment_below == null ? '' : dq.enrolment_below);
    setEnrolmentAbove(dq.enrolment_above === '' || dq.enrolment_above == null ? '' : dq.enrolment_above);
    setSectorNotIn(asListSafe(dq.sector_not_in));
    setCountryNotIn(asListSafe(dq.country_not_in));
    setFinancialBand(dq.financial_band_below || '');
    setAiReadiness(dq.ai_readiness_below === '' || dq.ai_readiness_below == null ? '' : dq.ai_readiness_below);
    setCompetitorLocked(asListSafe(dq.competitor_locked));
    {
      const ls = asListSafe(lensRecord.sectors);
      setLensSectorList(ls);
      const notIn = asListSafe(dq.sector_not_in);
      setSelectedSectors(ls.filter((x) => !notIn.includes(x)));
    }
  };

  useEffect(() => {
    let cancelled = false;
    if (!open || !tenantId) return;
    setLoading(true);
    setPreview(null);
    Promise.all([
      fetchMarketPreferences(tenantId, lens),
      fetchMarketPlayers(tenantId, lens).catch(() => null),
      fetchMarketLenses(tenantId).catch(() => ({ lenses: [] })),
      fetchMarketOpportunities(tenantId, { lens, limit: 200 }).catch(() => ({})),
      AccountFinal.filter({ tenant_id: tenantId }, '', 500).catch(() => []),
    ])
      .then(([data, playersData, lensesData, oppsData, accounts]) => {
        if (cancelled) return;
        setPlayers(playersData);
        setActiveLens(lens || null);
        setLenses((lensesData?.lenses || []).length ? (lensesData.lenses) : lensesProp);
        const sectors = new Set();
        unwrapRows(oppsData).forEach((r) => { if (r?.sector) sectors.add(String(r.sector).trim()); });
        (accounts || []).forEach((a) => { if (a?.sector) sectors.add(String(a.sector).trim()); });
        setSectorOptions([...sectors].filter(Boolean).sort((a, b) => a.localeCompare(b)));
        applyLoadedPrefs(data);
      })
      .catch((e) => {
        if (!cancelled) {
          // translate() (not the hook's t) so this effect's dependency list is unchanged.
          notify({ title: translate('mktCouldNotLoadPreferences'), description: e.message, variant: 'destructive' });
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, tenantId, lens, lensesProp]);

  const categories = entitlementCategories(prefs);
  const categoryOptions = useMemo(() => {
    const opts = productCategoryOptions(lenses, categories);
    if (category && !opts.some((o) => o.value === category)) {
      return [...opts, { value: category, label: formatCategoryLabel(category) }];
    }
    return opts;
  }, [lenses, categories, category]);
  /* Every sector the tenant may choose: the entitlement when it lists them, otherwise what the lens and the
   * loaded accounts show. A lens saved with NO sectors means all of them, so it opens with everything ticked. */
  const sectorUniverse = useMemo(() => {
    const ent = asListSafe(prefs?.entitlement?.sectors);
    const set = new Set(ent.length ? ent : [...lensSectorList, ...sectorOptions]);
    lensSectorList.forEach((x) => { if (x) set.add(x); });
    return [...set].filter(Boolean).sort((a, b) => a.localeCompare(b));
  }, [prefs, lensSectorList, sectorOptions]);
  useEffect(() => {
    if (!touched.sectors && lensSectorList.length === 0 && sectorUniverse.length) setSelectedSectors(sectorUniverse);
  }, [lensSectorList, sectorUniverse, touched.sectors]);
  const changeSectors = (next) => {
    markTouched('sectors');
    // The exclusion rule this replaces is cleared with it, so the two can never disagree.
    if (sectorNotIn.length) { markTouched('disqualifiers'); setSectorNotIn([]); }
    setSelectedSectors(next);
  };
  const countries = entitlementCountries(prefs);
  const versions = rubricVersions(prefs);
  const sum = weightsSum(weights);
  const editAllowed = Boolean(canEdit || prefs?.can_edit);

  const lensKey = activeLens || lens;

  const vendorMatchCounts = useMemo(() => {
    const map = {};
    const cats = players?.categories || [];
    const relevant = category
      ? cats.filter((c) => c.category === category || c.key === category)
      : cats;
    (relevant.length ? relevant : cats).forEach((cat) => {
      (cat.vendors || []).forEach((v) => {
        const key = v.label || v.key;
        map[key] = (map[key] || 0) + (v.count || 0);
      });
    });
    return map;
  }, [players, category]);

  const competitorCountriesKey = lensCountries.join(',');
  const competitorVendorsKey = competitorSet.join(',');

  useEffect(() => {
    if (!open || !tenantId || !lensKey || !competitorSet.length) {
      if (!competitorSet.length) {
        setCompetitorStats({});
      }
      return undefined;
    }
    let cancelled = false;
    fetchVendorMatches(tenantId, lensKey, competitorSet, lensCountries)
      .then((data) => {
        if (cancelled) return;
        const rows = data?.vendors || [];
        const next = {};
        rows.forEach((v) => {
          if (v?.vendor) next[v.vendor] = v;
        });
        setCompetitorStats(next);
        if (data?.addressable_accounts != null) {
          setAddressableAccounts(Number(data.addressable_accounts));
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [open, tenantId, lensKey, competitorCountriesKey, competitorVendorsKey]);

  const handleProductCategoryChange = (key) => {
    const match = lenses.find((l) => l.lens_key === key);
    const nextLens = match?.lens_key || key;
    if (!nextLens || nextLens === lensKey) return;
    setActiveLens(nextLens);
    setPreview(null);
    applyLensSnapshot(match);
    const requestId = ++lensRequestId.current;
    setLensLoading(true);
    Promise.all([
      fetchMarketPreferences(tenantId, nextLens),
      fetchMarketPlayers(tenantId, nextLens).catch(() => null),
      fetchMarketOpportunities(tenantId, { lens: nextLens, limit: 200 }).catch(() => ({})),
    ])
      .then(([data, playersData, oppsData]) => {
        if (requestId !== lensRequestId.current) return;
        applyLoadedPrefs(data);
        setPlayers(playersData);
        const sectors = new Set();
        unwrapRows(oppsData).forEach((r) => { if (r?.sector) sectors.add(String(r.sector).trim()); });
        sectorOptions.forEach((s) => { if (s) sectors.add(s); });
        setSectorOptions([...sectors].filter(Boolean).sort((a, b) => a.localeCompare(b)));
      })
      .catch((e) => {
        if (requestId !== lensRequestId.current) return;
        notify({ title: t('mktCouldNotLoadCategory'), description: e.message, variant: 'destructive' });
      })
      .finally(() => {
        if (requestId === lensRequestId.current) setLensLoading(false);
      });
  };

  const sparsePatch = () => {
    if (loading || !prefs) return null;
    const body = {};
    const lens = {};
    if (touched.category && category) lens.category = category;
    if (touched.sectors && selectedSectors.length > 0) lens.sectors = selectedSectors;
    if (touched.countries) {
      const allowed = countries.length
        ? lensCountries.filter((c) => countries.includes(c))
        : lensCountries;
      if (allowed.length > 0) lens.countries = allowed;
    }
    if (touched.competitors) {
      lens.competitor_set = competitorSet;
      body.competitor_set = competitorSet;
    }
    if (Object.keys(lens).length) body.lens = lens;
    if (touched.sam) {
      body.sam = {
        sam_min_pain: Number(samPain),
        score_threshold: Number(scoreThreshold),
      };
    }
    if (touched.disqualifiers) {
      body.disqualifiers = [
        { rule: 'is_current_vendor', enabled: isCurrentVendor },
        { rule: 'enrolment_below', value: enrolmentBelow === '' ? null : Number(enrolmentBelow), enabled: enrolmentBelow !== '' },
        { rule: 'enrolment_above', value: enrolmentAbove === '' ? null : Number(enrolmentAbove), enabled: enrolmentAbove !== '' },
        { rule: 'sector_not_in', value: sectorNotIn, enabled: sectorNotIn.length > 0 },
        { rule: 'country_not_in', value: countryNotIn, enabled: countryNotIn.length > 0 },
        { rule: 'financial_band_below', value: financialBand || null, enabled: Boolean(financialBand) },
        { rule: 'ai_readiness_below', value: aiReadiness === '' ? null : Number(aiReadiness), enabled: aiReadiness !== '' },
        { rule: 'competitor_locked', value: competitorLocked, enabled: competitorLocked.length > 0 },
      ];
    }
    if (touched.weights && weights.length) body.weights = weightsToPayload(weights);
    return Object.keys(body).length ? body : null;
  };

  const runPreview = async () => {
    const body = sparsePatch();
    if (!body) {
      notify({ title: t('mktNothingToPreviewTitle'), description: t('mktChangeAFilterFirst'), variant: 'destructive' });
      return;
    }
    setPreviewing(true);
    try {
      const data = await previewMarketPreferences(tenantId, body, lensKey);
      setPreview(data);
    } catch (e) {
      notify({ title: t('mktPreviewFailed'), description: e.message, variant: 'destructive' });
    } finally {
      setPreviewing(false);
    }
  };

  const commit = async (extra = {}) => {
    if (loading || !prefs) return;
    if (touched.competitors && competitorSet.length === 0) {
      const ok = window.confirm(t('mktConfirmEmptyCompetitorSet'));
      if (!ok) return;
    }
    const body = { ...(sparsePatch() || {}), ...extra, defer_recompute: true };
    if (!Object.keys(body).length) return;
    setSaving(true);
    try {
      const { status, data } = await patchMarketPreferences(tenantId, body, lensKey);

      if (status === 202) {
        setTouched({});
        onOpenChange?.(false);
        onRescoring?.(true);
        notify({
          title: t('mktPreferencesSaved'),
          description: t('mktMarketIsRescoring'),
          variant: 'success',
        });
        try {
          const outcome = await pollMarketRecomputeStatus(tenantId, { scope: extra?.scope });
          if (outcome.state === 'done' && outcome.error) {
            notify({
              title: t('mktSavedRescoreFailed'),
              description: outcome.warning || outcome.error || t('mktScoresMayReflectPrevious'),
              variant: 'destructive',
            });
          } else if (outcome.timedOut) {
            // Still running past the client's patience: say so rather than reload onto the old scores as if done.
            notify({ title: t('mktRescoreStillRunning'), description: t('mktRescoreStillRunningHint'), variant: 'default' });
          } else if (outcome.state === 'unknown' || outcome.changed) {
            const next = await fetchMarketPreferences(tenantId, lensKey).catch(() => null);
            if (next) applyLoadedPrefs(next);
            onSaved?.();
          }
        } finally {
          onRescoring?.(false);
        }
        return;
      }

      const next = await fetchMarketPreferences(tenantId, lensKey).catch(() => data?.preferences || data);
      if (next) applyLoadedPrefs(next);
      const recomputed = data?.recomputed;
      if (recomputed === false) {
        notify({
          title: t('mktSavedRescoreFailed'),
          description: data?.warning || data?.message || t('mktScoresMayReflectPrevious'),
          variant: 'destructive',
        });
      } else {
        const rec = recomputed && typeof recomputed === 'object'
          ? ` ${t('mktRecomputedCounts', { sam: recomputed.sam ?? '—', priority: recomputed.priority ?? '—' })}`
          : '';
        notify({ title: t('mktPreferencesSaved'), description: `${t('mktMarketRecomputed')}${rec}`, variant: 'success' });
      }
      setTouched({});
      onSaved?.();
    } catch (e) {
      const msg = e?.status === 403
        ? (e?.body?.message || e?.body?.error || e.message)
        : (e?.body?.error || e?.body?.message || e.message);
      notify({
        title: e?.status === 403 ? t('mktCouldNotSave') : (e?.status === 400 ? t('mktCouldNotSave') : t('saveFailed')),
        description: msg,
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const revertToTeam = async () => {
    if (!tenantId || reverting) return;
    setReverting(true);
    try {
      const data = await deleteMyMarketLens(tenantId, lensKey);
      notify({
        title: t('mktUsingTeamSettings'),
        description: data?.note || t('mktBackOnTeamSettings'),
        variant: 'success',
      });
      const next = await fetchMarketPreferences(tenantId, lensKey).catch(() => null);
      if (next) applyLoadedPrefs(next);
      onSaved?.();
    } catch (e) {
      notify({
        title: t('mktCouldNotSwitch'),
        description: e?.body?.message || e?.body?.error || e.message,
        variant: 'destructive',
      });
    } finally {
      setReverting(false);
    }
  };

  const rollback = async (version) => {
    await commit({ rubric_version: version.id || version.version || version });
  };

  const recalc = async () => {
    setRecalcStatus('running');
    try {
      const data = await recalculateMarket(tenantId, {}, lensKey);
      const status = data?.status;
      setRecalcStatus(status === 'complete' ? 'complete' : status || 'complete');
      if (status === 'complete' || !status) {
        notify({ title: t('mktRecalculationComplete'), variant: 'success' });
        onSaved?.();
      }
    } catch (e) {
      setRecalcStatus(null);
      notify({ title: t('mktRecalculateFailed'), description: e.message, variant: 'destructive' });
    }
  };

  const toggleCountry = (c) => {
    markTouched('countries');
    setLensCountries((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]));
  };

  const moved = preview?.moved || preview?.preview?.moved;
  const before = preview?.before ?? preview?.preview?.before;
  const after = preview?.after ?? preview?.preview?.after;
  const sampleOut = preview?.sample_out || preview?.preview?.sample_out || [];
  const excludedTotal = excluded?.total ?? 0;
  const heldTotal = held?.total ?? 0;
  const hasChanges = Boolean(sparsePatch());
  const showLms = Array.isArray(lmsFamilies) && lmsFamilies.length > 0 && typeof onLmsVendorsChange === 'function';
  const isMyLens = Boolean(prefs?.is_my_lens);
  const canEditTeamLens = Boolean(prefs?.can_edit_team_lens);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="text-[#0A1A2F]">
            {forceSetup ? t('mktSetUpMarket') : t('mktMarketPreferences')}
          </SheetTitle>
          <SheetDescription>
            {!prefs
              ? t('mktPrefsIntro')
              : isMyLens
                ? t('mktPrefsOnOwnSettings')
                : t('mktPrefsOnTeamSettings')}
          </SheetDescription>
        </SheetHeader>
        {isMyLens && !loading ? (
          <Button
            variant="outline"
            size="sm"
            className="mt-4"
            disabled={reverting}
            onClick={() => void revertToTeam()}
          >
            {reverting ? t('mktSwitching') : t('mktUseTeamSettings')}
          </Button>
        ) : null}

        {loading ? (
          <div className="mt-6 h-40 bg-gray-100 rounded-md animate-pulse" />
        ) : (
          <div className="mt-4 space-y-6">
          {/* [2026-11-04] One accordion, in the order Tiago set: sectors, product, countries, LMS, competitors, then the
              scoring settings. Each header carries a one-line summary so the drawer reads closed. Sectors are chosen
              positively here (the lens's own sector list); the old "Sector exclude" disqualifier is gone from the UI. */}
          <Accordion type="multiple" defaultValue={['sectors']} className={lensLoading ? 'opacity-60' : ''}>
            <AccordionItem value="sectors">
              <AccordionTrigger className="py-3 hover:no-underline">
                <span className="flex flex-col items-start gap-0.5">
                  <span className="text-xs font-semibold uppercase tracking-wide text-[#5C6B7A]">{t('mktSectors')}</span>
                  <span className="text-xs font-normal text-[#0A1A2F]">{t('mktNOfMSelected', { n: selectedSectors.length, m: sectorUniverse.length })}</span>
                </span>
              </AccordionTrigger>
              <AccordionContent>
              <p className="text-xs text-[#5C6B7A] mb-2">{t('mktSectorsHint')}</p>
              {editAllowed && sectorUniverse.length > 1 ? (
                <div className="flex gap-3 mb-2">
                  <button type="button" className="text-xs text-[#00C2C2] hover:underline" onClick={() => changeSectors(sectorUniverse)}>{t('mktSelectAll')}</button>
                  <button type="button" className="text-xs text-[#00C2C2] hover:underline" onClick={() => changeSectors([])}>{t('mktClearAll')}</button>
                </div>
              ) : null}
              <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                {sectorUniverse.length === 0 ? (
                  <p className="text-xs text-[#5C6B7A]">{t('mktNoSectorValues')}</p>
                ) : sectorUniverse.map((sec) => (
                  <label key={sec} className="flex items-center gap-2 text-sm text-[#0A1A2F]">
                    <Checkbox
                      checked={selectedSectors.includes(sec)}
                      disabled={!editAllowed}
                      onCheckedChange={() => changeSectors(selectedSectors.includes(sec)
                        ? selectedSectors.filter((x) => x !== sec)
                        : [...selectedSectors, sec])}
                    />
                    {sec}
                  </label>
                ))}
              </div>
              {touched.sectors && selectedSectors.length === 0 ? (
                <p className="text-xs text-amber-800 mt-2">{t('mktChooseOneSector')}</p>
              ) : null}
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="product">
              <AccordionTrigger className="py-3 hover:no-underline">
                <span className="flex flex-col items-start gap-0.5">
                  <span className="text-xs font-semibold uppercase tracking-wide text-[#5C6B7A]">{t('mktProductCategory')}</span>
                  <span className="text-xs font-normal text-[#0A1A2F]">{lensDisplayName((lenses.find((l) => l.lens_key === lensKey) || {}).lens_name || lensKey || '')}</span>
                </span>
              </AccordionTrigger>
              <AccordionContent>
              <Select
                value={lensKey || undefined}
                onValueChange={handleProductCategoryChange}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('mktChooseProductCategory')} />
                </SelectTrigger>
                <SelectContent>
                  {(lenses.length
                    ? lenses.map((l) => ({
                      value: l.lens_key,
                      label: lensDisplayName(l.lens_name || l.lens_key),
                    }))
                    : categoryOptions
                  ).map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {lensLoading && (
                <p className="text-xs text-[#5C6B7A] mt-2">{t('mktUpdatingCompetitorSet')}</p>
              )}
              </AccordionContent>
            </AccordionItem>
            {countries.length > 0 && (
            <AccordionItem value="countries">
              <AccordionTrigger className="py-3 hover:no-underline">
                <span className="flex flex-col items-start gap-0.5">
                  <span className="text-xs font-semibold uppercase tracking-wide text-[#5C6B7A]">{t('mktCountries')}</span>
                  <span className="text-xs font-normal text-[#0A1A2F]">{t('mktNOfMSelected', { n: lensCountries.length, m: countries.length })}</span>
                </span>
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-2">
                  {countries.map((c) => (
                    <label key={c} className="flex items-center gap-2 text-sm text-[#0A1A2F]">
                      <Checkbox checked={lensCountries.includes(c)} disabled={!editAllowed} onCheckedChange={() => toggleCountry(c)} />
                      {c}
                    </label>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
            )}
            {showLms ? (
            <AccordionItem value="lms">
              <AccordionTrigger className="py-3 hover:no-underline">
                <span className="flex flex-col items-start gap-0.5">
                  <span className="text-xs font-semibold uppercase tracking-wide text-[#5C6B7A]">LMS</span>
                  <span className="text-xs font-normal text-[#0A1A2F]">{lmsVendors.length ? t('mktNSelected', { n: lmsVendors.length }) : ''}</span>
                </span>
              </AccordionTrigger>
              <AccordionContent>
            <p className="text-xs text-[#5C6B7A] mb-2">
              {t('mktLmsFamiliesNote')}
            </p>
            <div className="space-y-2">
              {lmsFamilies.map((row) => {
                const family = row?.family;
                if (!family) return null;
                const checked = lmsVendors.includes(family);
                return (
                  <label key={family} className="flex items-center justify-between gap-2 text-sm text-[#0A1A2F]">
                    <span className="flex items-center gap-2 min-w-0">
                      <Checkbox
                        checked={checked}
                        onCheckedChange={(next) => {
                          const on = next === true;
                          const nextList = on
                            ? [...new Set([...lmsVendors, family])]
                            : lmsVendors.filter((v) => v !== family);
                          onLmsVendorsChange(nextList);
                        }}
                      />
                      {family}
                    </span>
                    <span className="text-[11px] text-[#5C6B7A] shrink-0">
                      {Number(row.count || 0).toLocaleString()}
                    </span>
                  </label>
                );
              })}
            </div>
              </AccordionContent>
            </AccordionItem>
            ) : null}
            <AccordionItem value="competitors">
              <AccordionTrigger className="py-3 hover:no-underline">
                <span className="flex flex-col items-start gap-0.5">
                  <span className="text-xs font-semibold uppercase tracking-wide text-[#5C6B7A]">{t('mktCompetitorSet')}</span>
                  <span className="text-xs font-normal text-[#0A1A2F]">{t('mktNSelected', { n: competitorSet.length })}</span>
                </span>
              </AccordionTrigger>
              <AccordionContent>
              <ul className="space-y-1 mb-2">
                {competitorSet.map((name) => {
                  const stats = competitorStats[name];
                  const count = stats?.accounts ?? vendorMatchCounts[name] ?? 0;
                  return (
                    <li key={name} className="text-sm">
                      <div className="flex items-center justify-between gap-2">
                        <p className="min-w-0 text-[#0A1A2F]">
                          {name}
                          <span className="text-[11px] text-[#5C6B7A]">
                            {' · '}{t('mktNAccounts', { n: count })}
                            {addressableAccounts != null
                              ? ` ${t('mktOfN', { n: addressableAccounts.toLocaleString() })}`
                              : ''}
                          </span>
                        </p>
                        {editAllowed && (
                          <button
                            type="button"
                            className="text-xs text-[#00C2C2] hover:underline shrink-0"
                            onClick={() => {
                              markTouched('competitors');
                              setCompetitorSet((prev) => prev.filter((x) => x !== name));
                            }}
                          >
                            {t('mktRemove')}
                          </button>
                        )}
                      </div>
                      {Array.isArray(stats?.matched_forms) && stats.matched_forms.length > 0 && (
                        <p className="text-[11px] text-[#5C6B7A]">{t('mktMatchesForms', { forms: stats.matched_forms.join(', ') })}</p>
                      )}
                    </li>
                  );
                })}
                {competitorSet.length === 0 && (
                  <p className="text-xs text-amber-800">{t('mktEmptyCompetitorSetWarning')}</p>
                )}
              </ul>
              <Textarea
                placeholder={t('mktPasteVendorsPlaceholder')}
                value={pasteBlock}
                disabled={!editAllowed}
                onChange={(e) => setPasteBlock(e.target.value)}
              />
              <Button
                variant="outline"
                size="sm"
                className="mt-2"
                disabled={!editAllowed || pasting}
                onClick={async () => {
                  const added = parseVendorList(pasteBlock);
                  if (!added.length) return;
                  markTouched('competitors');
                  setPasting(true);
                  try {
                    const data = await fetchVendorMatches(tenantId, lensKey, added, lensCountries);
                    const rows = data?.vendors || [];
                    setCompetitorStats((prev) => {
                      const next = { ...prev };
                      rows.forEach((v) => {
                        if (v?.vendor) next[v.vendor] = v;
                      });
                      return next;
                    });
                    if (data?.addressable_accounts != null) {
                      setAddressableAccounts(Number(data.addressable_accounts));
                    }
                    const names = rows.map((v) => v.vendor).filter(Boolean);
                    setCompetitorSet((prev) => [...new Set([...prev, ...(names.length ? names : added)])]);
                  } catch {
                    setCompetitorSet((prev) => [...new Set([...prev, ...added])]);
                  } finally {
                    setPasting(false);
                    setPasteBlock('');
                  }
                }}
              >
                {pasting ? t('mktMatching') : t('mktAddPastedVendors')}
              </Button>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="thresholds">
              <AccordionTrigger className="py-3 hover:no-underline">
                <span className="flex flex-col items-start gap-0.5">
                  <span className="text-xs font-semibold uppercase tracking-wide text-[#5C6B7A]">{t('mktTargetThresholds')}</span>
                  <span className="text-xs font-normal text-[#0A1A2F]">{`${t('mktMinimumPain')} ${samPain} · ${scoreThreshold}`}</span>
                </span>
              </AccordionTrigger>
              <AccordionContent>
              <div className="space-y-3">
                <label className="block text-sm">
                  <span className="text-xs text-[#5C6B7A]">
                    <MarketHelpLabel
                      label={t('mktMinimumPain')}
                      hint={t('mktMinimumPainHint')}
                    />
                  </span>
                  <Select
                    value={String(samPain === '' || samPain == null ? 3 : samPain)}
                    disabled={!editAllowed}
                    onValueChange={(v) => { markTouched('sam'); setSamPain(Number(v)); }}
                  >
                    <SelectTrigger className="h-8 mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[0, 1, 2, 3, 4, 5].map((n) => (
                        <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </label>
                <label className="block text-sm">
                  <span className="text-xs text-[#5C6B7A]">
                    <MarketHelpLabel
                      label={t('mktPriorityScoreThreshold')}
                      hint={t('mktPriorityScoreThresholdHint')}
                    />
                  </span>
                  <Input type="number" className="h-8 mt-1" value={scoreThreshold} disabled={!editAllowed} onChange={(e) => { markTouched('sam'); setScoreThreshold(e.target.value); }} />
                </label>
              </div>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="disqualifiers">
              <AccordionTrigger className="py-3 hover:no-underline">
                <span className="flex flex-col items-start gap-0.5">
                  <span className="text-xs font-semibold uppercase tracking-wide text-[#5C6B7A]">{t('mktDisqualifiers')}</span>
                  <span className="text-xs font-normal text-[#0A1A2F]">{t('mktNAccountsExcluded', { n: excludedTotal })}</span>
                </span>
              </AccordionTrigger>
              <AccordionContent>
              <p className="text-[11px] text-[#5C6B7A] mb-2">
                {t('mktNAccountsExcluded', { n: excludedTotal })}
                {heldTotal ? (
                  <>
                    {', '}
                    {t('mktNHeldPendingEnrolment', { n: heldTotal })}{' '}
                    <MarketHelpLabel
                      label={t('mktHeld')}
                      hint={t('mktHeldHint')}
                    />
                  </>
                ) : null}
                .
              </p>
              <div className="space-y-3">
                <label className="flex items-center justify-between text-sm gap-2">
                  <span>{t('mktExcludeCurrentCustomers')}</span>
                  <Switch checked={isCurrentVendor} disabled={!editAllowed} onCheckedChange={(v) => { markTouched('disqualifiers'); setIsCurrentVendor(v); }} />
                </label>
                <label className="block text-sm">
                  <span className="text-xs text-[#5C6B7A]">
                    <MarketHelpLabel
                      label={t('mktMinimumEnrolment')}
                      hint={t('mktMinimumEnrolmentHint')}
                    />
                  </span>
                  <Input type="number" className="h-8 mt-1" value={enrolmentBelow} disabled={!editAllowed} onChange={(e) => { markTouched('disqualifiers'); setEnrolmentBelow(e.target.value); }} />
                </label>
                <label className="block text-sm">
                  <span className="text-xs text-[#5C6B7A]">{t('mktMaximumEnrolment')}</span>
                  <Input type="number" className="h-8 mt-1" value={enrolmentAbove} disabled={!editAllowed} onChange={(e) => { markTouched('disqualifiers'); setEnrolmentAbove(e.target.value); }} />
                </label>
                <label className="block text-sm">
                  <span className="text-xs text-[#5C6B7A]">
                    <MarketHelpLabel
                      label={t('mktFinancialBandBelow')}
                      hint={t('mktFinancialBandBelowHint')}
                    />
                  </span>
                  <Select value={financialBand || 'none'} disabled={!editAllowed} onValueChange={(v) => { markTouched('disqualifiers'); setFinancialBand(v === 'none' ? '' : v); }}>
                    <SelectTrigger className="h-8 mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">{t('productFitNone')}</SelectItem>
                      <SelectItem value="Low">{mktLabel(t, 'Low')}</SelectItem>
                      <SelectItem value="Moderate">{mktLabel(t, 'Moderate')}</SelectItem>
                      <SelectItem value="High">{mktLabel(t, 'High')}</SelectItem>
                    </SelectContent>
                  </Select>
                </label>
                <label className="block text-sm">
                  <span className="text-xs text-[#5C6B7A]">{t('mktAiReadinessBelow')}</span>
                  <Select
                    value={aiReadiness === '' || aiReadiness == null ? 'none' : String(aiReadiness)}
                    disabled={!editAllowed}
                    onValueChange={(v) => {
                      markTouched('disqualifiers');
                      setAiReadiness(v === 'none' ? '' : v);
                    }}
                  >
                    <SelectTrigger className="h-8 mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">{t('productFitNone')}</SelectItem>
                      {[1, 2, 3, 4, 5].map((level) => (
                        <SelectItem key={level} value={String(level)}>
                          {level} - {mktLabel(t, AI_READINESS_LABELS[level])}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </label>
                <div>
                  <p className="text-xs text-[#5C6B7A] mb-1">
                    <MarketHelpLabel
                      label={t('mktCompetitorLocked')}
                      hint={t('mktCompetitorLockedHint')}
                    />
                  </p>
                  <ul className="space-y-1 mb-2">
                    {competitorLocked.map((name) => (
                      <li key={name} className="flex items-center justify-between gap-2 text-sm">
                        <span className="text-[#0A1A2F]">{name}</span>
                        {editAllowed && (
                          <button
                            type="button"
                            className="text-xs text-[#00C2C2] hover:underline shrink-0"
                            onClick={() => {
                              markTouched('disqualifiers');
                              setCompetitorLocked((prev) => prev.filter((x) => x !== name));
                            }}
                          >
                            {t('mktRemove')}
                          </button>
                        )}
                      </li>
                    ))}
                    {competitorLocked.length === 0 && (
                      <p className="text-xs text-[#5C6B7A]">{t('mktNothingLocked')}</p>
                    )}
                  </ul>
                  <Textarea
                    placeholder={t('mktPasteVendorsToLockPlaceholder')}
                    value={lockedPaste}
                    disabled={!editAllowed}
                    onChange={(e) => setLockedPaste(e.target.value)}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="mt-2"
                    disabled={!editAllowed}
                    onClick={() => {
                      const added = parseVendorList(lockedPaste);
                      if (!added.length) return;
                      markTouched('disqualifiers');
                      setCompetitorLocked((prev) => [...new Set([...prev, ...added])]);
                      setLockedPaste('');
                    }}
                  >
                    {t('mktLockPastedVendors')}
                  </Button>
                </div>
              </div>
              </AccordionContent>
            </AccordionItem>
            {weights.length > 0 && (
            <AccordionItem value="weights">
              <AccordionTrigger className="py-3 hover:no-underline">
                <span className="flex flex-col items-start gap-0.5">
                  <span className="text-xs font-semibold uppercase tracking-wide text-[#5C6B7A]">{t('mktRubricWeights')}</span>
                  <span className="text-xs font-normal text-[#0A1A2F]">{t('mktSumValue', { value: Number(sum).toFixed(2) })}</span>
                </span>
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-2">
                  {weights.map((w, i) => {
                    const meta = rubricWeightLabel(w.key);
                    return (
                    <div key={w.key} className="flex items-center gap-2">
                      <span className="text-xs text-[#5C6B7A] w-36 truncate" title={mktLabel(t, meta.hint)}>
                        <MarketHelpLabel label={mktLabel(t, meta.label)} hint={mktLabel(t, meta.hint)} />
                      </span>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        max="1"
                        className="h-8 w-24 text-xs"
                        value={w.value}
                        disabled={!editAllowed}
                        onChange={(e) => {
                          markTouched('weights');
                          setWeights(redistributeRubricWeights(weights, i, e.target.value));
                        }}
                      />
                    </div>
                    );
                  })}
                </div>
                <p className="text-xs mt-2 text-[#5C6B7A]">
                  {t('mktSumValue', { value: Number(sum).toFixed(2) })}
                </p>
              </AccordionContent>
            </AccordionItem>
            )}
            <AccordionItem value="history">
              <AccordionTrigger className="py-3 hover:no-underline">
                <span className="flex flex-col items-start gap-0.5">
                  <span className="text-xs font-semibold uppercase tracking-wide text-[#5C6B7A]">{t('mktRubricHistory')}</span>
                  <span className="text-xs font-normal text-[#0A1A2F]">{versions.length ? `v${versions[0].version || versions[0].id || 1}` : ''}</span>
                </span>
              </AccordionTrigger>
              <AccordionContent>
              {versions.length === 0 ? (
                <p className="text-sm text-[#5C6B7A]">{t('mktNoPriorVersions')}</p>
              ) : (
                <ul className="space-y-2">
                  {versions.map((v, i) => (
                    <li key={v.id || v.version || i} className="flex items-center justify-between text-sm border border-gray-100 rounded-md px-3 py-2">
                      <span>
                        v{v.version || v.id || i + 1}
                        {v.created_at ? ` · ${new Date(v.created_at).toLocaleDateString()}` : ''}
                        {i === 0 && <Badge variant="outline" className="ml-2 text-[10px]">{t('mktCurrentVersion')}</Badge>}
                      </span>
                      {editAllowed && i > 0 && (
                        <Button variant="ghost" size="sm" className="text-xs" onClick={() => rollback(v)}>
                          {t('mktRollBack')}
                        </Button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              </AccordionContent>
            </AccordionItem>
          </Accordion>

            {editAllowed && (
              <>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={runPreview} disabled={previewing || loading || !hasChanges}>
                {previewing ? t('mktPreviewing') : t('mktPreviewChange')}
              </Button>
              {canEditTeamLens ? (
                <>
                  <Button
                    variant="outline"
                    onClick={() => commit()}
                    disabled={saving || loading || !prefs || !hasChanges}
                  >
                    {saving ? t('mktSaving') : t('mktSaveForMe')}
                  </Button>
                  <Button
                    className="bg-[#00C2C2] hover:bg-[#00A8A8] text-white"
                    onClick={() => commit({ scope: 'team' })}
                    disabled={saving || loading || !prefs || !hasChanges}
                  >
                    {saving ? t('mktSaving') : t('mktSaveForEveryone')}
                  </Button>
                </>
              ) : (
                <Button
                  className="bg-[#00C2C2] hover:bg-[#00A8A8] text-white"
                  onClick={() => commit()}
                  disabled={saving || loading || !prefs || !hasChanges}
                >
                  {saving ? t('mktSaving') : t('save')}
                </Button>
              )}
            </div>

            {preview && (
              <section className="rounded-lg border border-gray-200 p-3 text-sm">
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <p className="text-[#5C6B7A] uppercase">{t('mktBefore')}</p>
                    <p className="mt-1 text-[#0A1A2F]">{t('mktPreviewCounts', { sam: before?.sam ?? '—', priority: before?.priority ?? '—', excluded: before?.excluded ?? '—' })}</p>
                  </div>
                  <div>
                    <p className="text-[#5C6B7A] uppercase">{t('mktAfter')}</p>
                    <p className="mt-1 text-[#0A1A2F]">{t('mktPreviewCounts', { sam: after?.sam ?? '—', priority: after?.priority ?? '—', excluded: after?.excluded ?? '—' })}</p>
                  </div>
                </div>
                {moved && (
                  <p className="text-xs text-[#5C6B7A] mt-2">
                    {t('mktPreviewMoved', { in: moved.in ?? 0, out: moved.out ?? 0, promoted: moved.promoted ?? 0, demoted: moved.demoted ?? 0 })}
                  </p>
                )}
                {sampleOut.length > 0 && (
                  <p className="text-xs text-[#5C6B7A] mt-2">
                    {t('mktLeaving', { names: sampleOut.slice(0, 4).map((r) => r.account_name || r.name).filter(Boolean).join(', ') })}
                  </p>
                )}
              </section>
            )}
              </>
            )}

            {editAllowed && (
            <Button variant="outline" onClick={recalc} disabled={recalcStatus === 'running'}>
              {recalcStatus === 'running' ? t('mktRecalculating') : recalcStatus === 'complete' ? t('mktRecalculated') : t('mktRecalculateMarket')}
            </Button>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function asListSafe(v) {
  if (Array.isArray(v)) return v.filter(Boolean);
  if (!v) return [];
  return String(v).split(',').map((s) => s.trim()).filter(Boolean);
}
